import React, { useEffect, useState } from 'react';
import { AnyObject } from 'final-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import _ from 'lodash';
import 'src/styles/pages/_accountsPayable.scss';
import ProspectStaff from 'src/models/projectSettings/TsdiProspectStaff';
import TsdiLandman from 'src/models/projectSettings/TsdiLandman';
import ProspectDetails from 'src/models/projectSettings/TsdiProspect';

interface IFindPersonDialogProps {
    dialogName: string;
    handleSelectedPerson?: (person: ProspectStaff) => void;
    hideDialog: () => void;
    populatePersonList: () => void;
    personList: Array<ProspectStaff>;
    setPersonSearchTerm?: (keyword: string) => void;
    createMode?: boolean;
    payPeriods?: Array<{value: number | string, label: string}>;
    workClassifications?: Array<{value: number | string, label: string}>;
    setSelectedProjectStaff?: (staffer: ProspectStaff) => void;
    project?: ProspectDetails;
}

const FindPersonDialog = (props: IFindPersonDialogProps) => {
    const { register, errors, getValues, handleSubmit, reset, formState, watch } = useForm();
    const [selectedPerson, setSelectedPerson] = useState<ProspectStaff>(undefined);
    const [storedFormData, setStoredFormData] = useState<AnyObject>(undefined);
    const [personnelSearchResults, setPersonnelSearchResults] = useState<Array<ProspectStaff>>(undefined); // The personnel filtered by search (Name)
    const [payPeriods, setPayPeriods] = useState<Array<{value: number | string, label: string}>>(undefined);
    const [workClassifications, setWorkClassifications] = useState<Array<{value: number | string, label: string}>>(undefined);
    const [addNewStaff, setAdNewStaff] = useState<boolean>(false);
    const { isDirty } = formState;

    useEffect(() => {
        onCreate();
        props.populatePersonList();
    }, []);

    useEffect(() => {
        if (props?.createMode) {
            setAdNewStaff(true);
        }
    }, [props?.createMode]);

    useEffect(() => {
        setPersonnelSearchResults(props.personList);
    }, [props.personList]);

    useEffect(() => {
        if (!!selectedPerson && !!storedFormData) {
            saveAndExit();
        }
    }, [selectedPerson, storedFormData]);

    useEffect(() => {
        if (personnelSearchResults) {
            console.log('PersonnelSearchResults Updated', personnelSearchResults);
        }
    }, [personnelSearchResults]);

    useEffect(() => {
        if (!!props?.payPeriods) {
            setPayPeriods(props?.payPeriods);
        }
    }, [props?.payPeriods]);

    useEffect(() => {
        if (!!props?.workClassifications) {
            setWorkClassifications(props?.workClassifications);
        }
    }, [props?.workClassifications]);

    const onCreate = () => {
        // getPersonnel();
        console.log('FindPersonDialog onCreate()');
    };

    const saveAndExit = () => {
        props.handleSelectedPerson(storedFormData as ProspectStaff);
        props.hideDialog();
    };

    const onSubmit = (formData: AnyObject) => {
        if (!isDirty || errors.tsdiEmployeeNumber || errors.lastName || errors.firstName || errors.workClassification ||
            errors.begPeriod || errors.endPeriod) {
            return;
        }
        const addStaffPayload: Partial<ProspectStaff> = {
            tsdiEmployeeNumber: !!selectedPerson.tsdiEmployeeNumber && !isNaN(Number(selectedPerson.tsdiEmployeeNumber)) ? Number(selectedPerson.tsdiEmployeeNumber) : 0,
            projectNumber: !!props?.project?.number && !isNaN(Number(props?.project?.number)) ? Number(props.project.number) : 0,
            firstName: formData?.firstName,
            lastName: formData?.lastName,
            begPeriod: !!formData?.begPeriod && !isNaN(Number(formData.begPeriod.replace('-', ''))) ? Number(formData.begPeriod.replace('-', '')) : 0,
            endPeriod: !!formData.endPeriod && !isNaN(Number(formData.endPeriod.replace('-', ''))) ? Number(formData.endPeriod.replace('-', '')) : 0,
            ownershipAccess: formData?.ownershipAccess,
            draftReport: formData?.emailDraftReport,
            catType: !!formData?.workClassification && !isNaN(Number(formData.workClassification)) ? Number(formData.workClassification) : 0,
            reportsAccess: formData?.notifyNewTracts,
            webMapping: formData?.webMapping,
            contractsAccess: formData?.contractsAccess
        };
        setStoredFormData(addStaffPayload);
    };

    const findWorkClassificationValueByLabel = (labelToFind: string): number | undefined => {
        let match;
        if (!!props?.workClassifications) {
            match = props.workClassifications.find(({ label }) => label === labelToFind);
        }
        return match ? Number(match.value) : undefined;
    };

    const setFormValues = (selectedStaff: ProspectStaff & { workClassification: number } ) => {
        const addStaffPayload: Partial<ProspectStaff> & { workClassification: number } = {
            tsdiEmployeeNumber: !isNaN(Number(selectedStaff.tsdiEmployeeNumber)) ? Number(selectedStaff.tsdiEmployeeNumber) : 0,
            firstName: selectedStaff.firstName,
            lastName: selectedStaff.lastName,
            middleName: selectedStaff.middleName,
            workClassification: findWorkClassificationValueByLabel(selectedStaff.classification)
        };
        reset(addStaffPayload);
        setSelectedPerson(selectedStaff);
    };

    const search = _.debounce(() => {
        let formData = getValues();
        let personSearchCriteria = (formData.tsdiEmployeeNumber ? formData.tsdiEmployeeNumber : '') + ',';
        personSearchCriteria += (formData.lastName ? formData.lastName : '') + ',';
        personSearchCriteria += formData.firstName ? formData.firstName : '';
        props.setPersonSearchTerm(personSearchCriteria);
    }, 800);

    const resetStaffSearchForm = () => {
        setSelectedPerson(undefined);
        reset({ tsdiEmployeeNumber: null, lastName: null, firstName: null, middleName: null });
    };

    const firstName = watch('firstName');
    const projectNumber = watch('projectNumber');

    const getSubmitButtonText = () => {
        const name = !!firstName ? firstName : '';
        const buttonProjectNumber = !!projectNumber ? projectNumber : '';
        const nameAndProject = (!!name ? name : '') + (!!buttonProjectNumber ? buttonProjectNumber : '');
        const submitButtonText = `Add ${nameAndProject || 'staff to project'}`;
        return submitButtonText;
    };

    /**
     * Gets the list of Personnel Users and stores in personnelList
     */
/*     const getPersonnel = async () => {
        let result = await get(`/v1.0/landmen`);
        if (response.ok && result) {
            setPersonnelList(result.items);
            setPersonnelSearchResults(result.items);
        } else {
            console.log('Unable to get the Personnel Data!!! >:c');
        }
    }; */

    return (
        <Dialog title={!addNewStaff ? 'Find Personnel' : 'Add Project Staff'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '55%' }}>
            <div className="form-row" style={{marginTop: '1rem', display: 'inline-block', whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
                <div style={{padding: '0px', margin: '0px', display: 'flex', whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
                    <RHFInputBox
                        labelOnLeft={true}
                        label={'LM #'}
                        type={'number'}
                        name={'tsdiEmployeeNumber'}
                        register={register}
                        style={{width: '100%'}}
                        bodyStyle={{width: '25%'}}
                        onChange={() => search()}
                        rules={{ required: 'LM # required' }}
                        readOnly={!!selectedPerson}
                        onDoubleClick={() => resetStaffSearchForm()}
                        title={'Double-click to reset selection.'}
                    />
                    <RHFInputBox
                        labelOnLeft={true}
                        label="Last Name"
                        name={'lastName'}
                        register={register} 
                        errors={errors}
                        style={{width: '100%'}}
                        bodyStyle={{width: '55%'}}
                        onChange={() => search()}
                        rules={{ required: 'full name required' }}
                        readOnly={!!selectedPerson}
                        onDoubleClick={() => resetStaffSearchForm()}
                    />
                    <RHFInputBox
                        labelOnLeft={true}
                        label="First Name"
                        name={'firstName'}
                        register={register} 
                        errors={errors}
                        style={{width: '100%'}}
                        bodyStyle={{width: '55%'}}
                        onChange={() => search()}
                        rules={{ required: 'full name required' }}
                        readOnly={!!selectedPerson}
                        onDoubleClick={() => resetStaffSearchForm()}
                    />
                    <RHFInputBox
                        labelOnLeft={true}
                        label="Middle Name"
                        name={'middleName'}
                        register={register} 
                        errors={errors}
                        style={{width: '100%'}}
                        bodyStyle={{width: '55%'}}
                        onChange={() => search()}
                        readOnly={!!selectedPerson}
                        onDoubleClick={() => resetStaffSearchForm()}
                    />
                </div>
                <div style={{padding: '0px', margin: '0px', display: 'flex', whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
                    <RHFSelect
                        labelOnLeft={true}
                        label={'Work Classification:'}
                        name={`workClassification`}
                        placeholder="Work Classification"
                        options={ !!workClassifications ? workClassifications : [
                            { value: 1, label: 'Project Manager' },
                            { value: 2, label: 'Mapper' },
                            { value: 3, label: 'Land Tech' },
                            { value: 4, label: 'Lease Records' },
                            { value: 5, label: 'Crew Chief' },
                            { value: 6, label: 'Title' },
                            { value: 7, label: 'Buyer' },
                            { value: 8, label: 'Curative' },
                            { value: 9, label: 'Right of Way' },
                            { value: 10, label: 'Admin' },
                            { value: 11, label: 'Trainee' },
                            { value: 12, label: 'Other' },
                            { value: 13, label: 'Project Coordinator' },
                        ]}
                        style={{width: '100%'}}
                        bodyStyle={{width: '50%'}}
                        register={register}
                        rules={{ required: 'Work Classification required' }}
                    />
                    {payPeriods && <RHFSelect
                        labelOnLeft={true}
                        label={'Beg Period:'}
                        options={payPeriods}
                        name={'begPeriod'}
                        register={register}
                        style={{width: '100%'}}
                        bodyStyle={{width: '50%'}}
                        rules={{ required: 'Beginning period required' }}
                    />}
                    {payPeriods && <RHFSelect
                        labelOnLeft={true}
                        label={'End Period:'}
                        options={payPeriods}
                        name={'endPeriod'}
                        register={register}
                        style={{width: '100%'}}
                        bodyStyle={{width: '50%'}}
                        rules={{ required: 'Ending period required' }}
                    />}
                </div>
                <div style={{padding: '0px', margin: '0px', display: 'flex', whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
                    <RHFInputBox
                        labelOnLeft={true}
                        label="Project #"
                        type={'number'}
                        defaultVal={!!props?.project?.number ? props.project.number.toString() : ''}
                        name={'projectNumber'}
                        style={{width: '100%'}}
                        bodyStyle={{width: '10%'}}
                        onChange={() => search()}
                        rules={{ required: 'Project required' }}
                        readOnly={true}
                    />
                    <RHFInputBox
                        labelOnLeft={true}
                        label="Project Name"
                        name={'projectName'}
                        register={register}
                        defaultVal={!!props?.project?.name ? props.project.name : ''}
                        style={{width: '100%'}}
                        bodyStyle={{width: '70%'}}
                        onChange={() => search()}
                        rules={{ required: 'Project required' }}
                        readOnly={true}
                    />
                </div>
                <div style={{display: 'flex', justifyContent: 'flex-start', flexDirection: 'row'}}>
                    {errors.tsdiEmployeeNumber && <p style={{ color: 'red' }}>{errors.tsdiEmployeeNumber.message}</p>}
                    {errors.lastName && <p style={{ color: 'red' }}>{errors.lastName.message}</p>}
                    {errors.firstName && <p style={{ color: 'red' }}>{errors.firstName.message}</p>}
                    {errors.workClassification && <p style={{ color: 'red' }}>{errors.workClassification.message}</p>}
                    {errors.begPeriod && <p style={{ color: 'red' }}>{errors.begPeriod.message}</p>}
                    {errors.endPeriod && <p style={{ color: 'red' }}>{errors.endPeriod.message}</p>}
                    {errors.projectName && <p style={{ color: 'red' }}>{errors.projectName.message}</p>}
                    {errors.projectNumber && <p style={{ color: 'red' }}>{errors.projectNumber.message}</p>}
                </div>
                <div style={{ width: '100%', textAlign: 'center', padding: '0', margin: '0' }}>
                    <h2>Access Permissions</h2>
                </div>
                <div style={{padding: '0px', margin: '0px', display: 'flex', whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
                    <RHFCheckbox
                        label={'Ownerships'}
                        register={register}
                        name={'ownershipsAccess'}
                    />
                    <RHFCheckbox
                        label={'Dr Report'}
                        register={register}
                        name={'contractsAccess'}
                    />
                    <RHFCheckbox
                        label={'Contracts'}
                        register={register}
                        name={'draftReport'}
                    />
                    <RHFCheckbox
                        label={'Reports'}
                        register={register}
                        name={'reportsAccess'}
                    />
                    <RHFCheckbox
                        label={'GIS'}
                        register={register}
                        name={'webMapping'}
                    />
                </div>
            </div>
            
                <form onSubmit={handleSubmit(onSubmit)}>
                    <table className={'project-billing'}>
                        <thead className={'project-billing'} style={{ textAlign: 'left' }}>
                            <tr className={'project-billing'}>
                                <th className="find-person-header" style={{ width: '8%' }}>LM #</th>
                                <th className="find-person-header" style={{ width: '12%' }}>Last Name</th>
                                <th className="find-person-header" style={{ width: '12%' }}>First Name</th>                                
                                <th className="find-person-header" style={{ width: '12%' }}>Middle Name</th>
                                <th className="find-person-header" style={{ width: '25%' }}>Corp</th>
                            </tr>
                        </thead>
                        <div style={{overflowY: 'scroll', height: '20rem'}}>
                        {personnelSearchResults?.map((person: TsdiLandman & { workClassification: number }, index: number) => {
                            return (
                                <tbody key={person.tsdiEmployeeNumber}>
                                    <tr 
                                        className={'project-billing table-item-hover'}
                                        style={person?.tsdiEmployeeNumber === selectedPerson?.tsdiEmployeeNumber ?
                                            {height: '2rem', background: 'yellow', textAlign: 'left' } : {height: '2rem', textAlign: 'left' }} 
                                        onClick={() => setFormValues(person)}
                                    >
                                        <td style={{ width: '8%' }}>
                                            <label>{person?.tsdiEmployeeNumber}</label>
                                        </td>
                                        <td style={{ width: '12%' }}>
                                            <label>{person?.lastName}</label>
                                        </td>
                                        <td style={{ width: '12%' }}>
                                            <label>{person?.firstName}</label>
                                        </td>
                                        <td style={{ width: '12%' }}>
                                            <label>{person?.middleName}</label>
                                        </td>
                                        <td style={{ width: '25%' }}>
                                            <label>{person?.corpName}</label>
                                        </td>
                                    </tr>
                                </tbody>
                                );
                            })}
                        </div>
                    </table>
                    <div className="form-row" style={{marginTop: '2rem', justifyContent: 'center'}}>
                        <Button
                            className="blue-hover-button"
                            style={{ width: '12%', marginRight: '1rem', whiteSpace: 'nowrap' }}
                            text={props?.createMode ? getSubmitButtonText() : 'OK'}
                            type={'submit'}
                        />
                        <Button
                            className="blue-hover-button"
                            style={{ width: '12%', marginRight: '1rem' }}
                            text={'Cancel'}
                            onClick={() => props.hideDialog()}
                        />
                    </div>
                </form>
        </Dialog>
    );
};

export default FindPersonDialog;



// WEBPACK FOOTER //
// ./src/components/client-project/dialogs/FindProjectStaffDialog.tsx